import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

const DEFAULT_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave this page?';

const GUARD_STATE_KEY = '__rmUnsavedGuard';

// Shared across hook instances so two dirty forms (e.g. customer page + create-client
// modal) do not stack dummy history entries or show the confirm twice.
let dirtyInstanceCount = 0;
const handledEvents = new WeakSet();

const markEventHandled = (event) => {
  if (handledEvents.has(event)) return false;
  handledEvents.add(event);
  return true;
};

const hasGuardOnTop = () => Boolean(window.history.state?.[GUARD_STATE_KEY]);

const isModifiedClick = (event) =>
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  event.button !== 0;

const isInternalAnchorNavigation = (anchor) => {
  if (!anchor || (anchor.target && anchor.target !== '_self')) return false;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) return false;
  try {
    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    if (
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const isInAppNavigationClick = (event) => {
  if (event.defaultPrevented || isModifiedClick(event)) return false;
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return false;

  const anchor = target.closest('a[href]');
  if (anchor) return isInternalAnchorNavigation(anchor);

  // Sidebar uses div.rm-nav-link + navigate(), not <a>.
  const sidebarLink = target.closest('.rm-nav-link');
  if (sidebarLink && !sidebarLink.classList.contains('rm-nav-link-active')) {
    return true;
  }

  return false;
};

/**
 * Warns before losing unsaved edits on:
 * - tab close / refresh (beforeunload)
 * - in-app route changes (useBlocker + click capture for sidebar)
 * - browser back / back gesture (history guard — needed because Dashboard
 *   still nests <Routes> under a /* splat, where useBlocker POP often no-ops)
 */
export const useUnsavedChangesWarning = (isDirty, message = DEFAULT_MESSAGE) => {
  const isDirtyRef = useRef(isDirty);

  const messageRef = useRef(message);
  messageRef.current = message;

  const allowNextNavigationRef = useRef(false);
  const bypassRef = useRef(false);
  const promptInFlightRef = useRef(false);
  const guardedHrefRef = useRef(null);
  const beforeUnloadHandlerRef = useRef(null);

  // Keep the bypass sticky: a parent re-render after save can write `isDirty`
  // back onto the ref before navigate() finishes (especially navigate(-1)).
  if (!bypassRef.current) {
    isDirtyRef.current = isDirty;
  }

  const detachBeforeUnload = useCallback(() => {
    if (!beforeUnloadHandlerRef.current) return;
    window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
    beforeUnloadHandlerRef.current = null;
  }, []);

  const allowNextNavigation = useCallback(() => {
    bypassRef.current = true;
    allowNextNavigationRef.current = true;
    isDirtyRef.current = false;
    // Drop the browser dialog so a confirmed in-app leave (back, save, sidebar)
    // does not also show "Changes that you made may not be saved."
    detachBeforeUnload();
  }, [detachBeforeUnload]);

  const confirmDiscard = useCallback(() => {
    if (!isDirtyRef.current) return true;
    const confirmed = window.confirm(messageRef.current);
    if (confirmed) {
      allowNextNavigation();
    }
    return confirmed;
  }, [allowNextNavigation]);

  // PUSH/REPLACE only (Link, navigate, sidebar). Back/POP is the history guard —
  // if useBlocker also handles POP we can get two window.confirm dialogs.
  const blocker = useBlocker(
    useCallback(({ historyAction }) => {
      if (bypassRef.current || allowNextNavigationRef.current || !isDirtyRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }
      if (historyAction === 'POP') return false;
      return Boolean(isDirty);
    }, [isDirty])
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      promptInFlightRef.current = false;
      return;
    }
    if (promptInFlightRef.current) return;
    promptInFlightRef.current = true;

    if (confirmDiscard()) {
      blocker.proceed?.();
    } else {
      blocker.reset?.();
    }
  }, [blocker, confirmDiscard]);

  // One dirty-lifecycle for back-guard, sidebar/link clicks, and refresh.
  useEffect(() => {
    if (!isDirty) return undefined;

    const pushGuard = () => {
      // Do not copy React Router's `idx` — a duplicate idx breaks POP deltas.
      // Skip if a guard is already on top (re-render / second dirty instance).
      if (hasGuardOnTop()) return;
      guardedHrefRef.current = window.location.href;
      window.history.pushState({ [GUARD_STATE_KEY]: true }, '', window.location.href);
    };

    dirtyInstanceCount += 1;
    pushGuard();

    const onPopState = (event) => {
      const leavingWithoutPrompt =
        bypassRef.current || allowNextNavigationRef.current || !isDirtyRef.current;

      if (leavingWithoutPrompt) {
        allowNextNavigationRef.current = false;
        markEventHandled(event);
        // navigate(-1) pops the dummy guard (same URL). Continue one more step
        // so save/cancel actually leave the page.
        const poppedGuardOnly =
          Boolean(guardedHrefRef.current) &&
          window.location.href === guardedHrefRef.current &&
          !hasGuardOnTop();
        if (poppedGuardOnly) {
          guardedHrefRef.current = null;
          window.history.back();
        }
        return;
      }

      if (!markEventHandled(event)) return;

      if (confirmDiscard()) {
        // Guard entry was already popped; one more back leaves the page.
        window.history.back();
      } else {
        pushGuard();
      }
    };

    const handleClick = (event) => {
      if (!isInAppNavigationClick(event) || !isDirtyRef.current) return;
      if (!markEventHandled(event)) return;
      if (confirmDiscard()) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    const handleBeforeUnload = (event) => {
      // Tab close / refresh only. In-app back is handled by popstate.
      if (bypassRef.current || !isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    beforeUnloadHandlerRef.current = handleBeforeUnload;
    window.addEventListener('popstate', onPopState);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', handleClick, true);
      if (beforeUnloadHandlerRef.current === handleBeforeUnload) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        beforeUnloadHandlerRef.current = null;
      }

      dirtyInstanceCount = Math.max(0, dirtyInstanceCount - 1);
      // Only drop the guard when no other dirty instance still needs it.
      if (dirtyInstanceCount === 0 && !isDirtyRef.current && hasGuardOnTop()) {
        window.history.back();
      }
    };
  }, [isDirty, confirmDiscard]);

  return { confirmDiscard, allowNextNavigation };
};

export default useUnsavedChangesWarning;
